import React from "react";
import { MultiSelectDropdown } from "../../../components/custom/multi-select-dropdown";
import { Department } from "../../department/services/department-service";
import {
  FaultSlaConfig,
  SLA_APPLIES_LABEL,
  SLA_MAX_LEVEL,
  SLA_MILESTONES_BY_APPLY,
  SlaAppliesTo,
  SlaMilestoneMeta,
} from "../fault-sla-config";

interface FaultSlaEditorProps {
  value: FaultSlaConfig;
  departments: Department[];
  onChange: (next: FaultSlaConfig) => void;
}

const GROUP_ACCENT: Record<SlaAppliesTo, string> = {
  NORMAL: "border-blue-200 bg-blue-50 text-blue-700",
  COUNTING: "border-amber-200 bg-amber-50 text-amber-700",
};

/**
 * Bảng cấu hình mốc phạt SLA: mỗi mốc 1 khối, gồm số giờ + 3 lần leo thang (mỗi lần: bộ phận chịu trách
 * nhiệm + số điểm). Danh mục mốc cố định (SLA_MILESTONES); người dùng chỉ chỉnh giờ/bộ phận/điểm.
 */
export const FaultSlaEditor: React.FC<FaultSlaEditorProps> = ({ value, departments, onChange }) => {
  const patchMilestone = (code: string, patch: Partial<{ hours: number }>) => {
    onChange({
      ...value,
      milestones: {
        ...value.milestones,
        [code]: { ...value.milestones[code], ...patch },
      },
    });
  };

  const patchLevel = (
    code: string,
    levelIdx: number,
    patch: Partial<{ department_ids: number[]; points: number }>,
  ) => {
    const milestone = value.milestones[code];
    const levels = milestone.levels.map((lv, i) => (i === levelIdx ? { ...lv, ...patch } : lv));
    onChange({
      ...value,
      milestones: { ...value.milestones, [code]: { ...milestone, levels } },
    });
  };

  const renderMilestone = (meta: SlaMilestoneMeta) => {
    const cfg = value.milestones[meta.code];
    return (
      <div key={meta.code} className="border border-slate-200 rounded-lg overflow-hidden">
        {/* Header khối: tên mốc + số giờ */}
        <div className="flex flex-wrap items-center gap-3 justify-between bg-slate-50 px-4 py-3 border-b border-slate-100">
          <div className="min-w-0">
            <span className="text-sm font-semibold text-slate-800">{meta.name}</span>
            <div className="text-xs text-slate-400 mt-0.5">
              {meta.from} → {meta.to}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Thời gian</span>
            <input
              type="number"
              min="0"
              value={cfg?.hours ?? ""}
              onChange={(e) => patchMilestone(meta.code, { hours: Number(e.target.value) || 0 })}
              className="w-20 border rounded-lg px-3 py-1.5 text-right focus:outline-none focus:ring-1 focus:ring-green-500"
            />
            <span className="text-xs text-slate-500">giờ</span>
          </div>
        </div>

        {/* 3 lần leo thang */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
          {Array.from({ length: SLA_MAX_LEVEL }).map((_, i) => {
            const lv = cfg?.levels?.[i] ?? { department_ids: [], points: 0 };
            const selectedDepts = departments.filter((d) => lv.department_ids.includes(d.id));
            return (
              <div key={i} className="border border-slate-100 rounded-lg p-3 bg-white">
                <div className="text-xs font-semibold text-slate-600 mb-2">Lần {i + 1}</div>
                <label className="text-[11px] text-slate-400 block mb-1">Bộ phận chịu trách nhiệm</label>
                <MultiSelectDropdown<Department>
                  placeholder="Chọn bộ phận"
                  dataOptions={departments}
                  optionSelected={selectedDepts}
                  getOptionKey={(d) => d.id}
                  getOptionLabel={(d) => d.name}
                  onChange={(selected) => patchLevel(meta.code, i, { department_ids: selected.map((d) => d.id) })}
                />
                <div className="flex items-center gap-2 mt-2">
                  <label className="text-[11px] text-slate-400 flex-1">Số điểm phạt</label>
                  <input
                    type="number"
                    min="0"
                    value={lv.points || ""}
                    onChange={(e) => patchLevel(meta.code, i, { points: Number(e.target.value) || 0 })}
                    placeholder="0"
                    className="w-20 border rounded-lg px-3 py-1.5 text-right focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                  <span className="text-[11px] text-slate-400">điểm</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Khoảng cách leo thang */}
      <div className="grid grid-cols-10 gap-4 items-center py-2">
        <div className="col-span-7">
          <div className="text-sm font-medium text-slate-700">Khoảng cách giữa các lần (giờ)</div>
          <div className="text-xs text-slate-400 mt-0.5">
            Sau khi quá mốc, cứ mỗi khoảng thời gian này hệ thống nâng 1 lần phạt (Lần 1 → 2 → 3). Sau Lần {SLA_MAX_LEVEL} dừng.
          </div>
        </div>
        <div className="col-span-3">
          <input
            type="number"
            min="1"
            value={value.escalation_interval_hours || ""}
            onChange={(e) => onChange({ ...value, escalation_interval_hours: Number(e.target.value) || 0 })}
            placeholder="VD: 2"
            className="w-full border rounded-lg px-3 py-2 text-right focus:outline-none focus:ring-1 focus:ring-green-500"
          />
        </div>
      </div>

      {/* Nhóm theo loại đơn: mỗi nhóm 1 bộ mốc riêng */}
      {SLA_MILESTONES_BY_APPLY.map((group) => (
        <div key={group.applies} className="space-y-3">
          <div className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-1 rounded-full border ${GROUP_ACCENT[group.applies]}`}>
            {SLA_APPLIES_LABEL[group.applies]}
            <span className="text-xs font-normal opacity-70">({group.milestones.length} mốc)</span>
          </div>
          <div className="space-y-3">{group.milestones.map(renderMilestone)}</div>
        </div>
      ))}
    </div>
  );
};
